import React, { useState, useCallback } from "react";
import { SearchParameters } from "../model/SearchParameters";
import { PriceRange } from "../model/PriceRange";

interface UseSearchAndPaginationResult {
  searchParams: SearchParameters;
  resetFilter: () => void;
  setSearchParams: React.Dispatch<React.SetStateAction<SearchParameters>>;
  handleFilterChange: (
    filter: string,
    value: string[] | string,
    brand: string | null,
  ) => void;
  setPriceRange: (priceRange: PriceRange[]) => void;
  handleSearch: (searchTerm: string) => void;
  handlePageChange: (newPage: number) => void;
}

const PAGINATION_LIMIT = 20;
const useSearchAndPagination = (): UseSearchAndPaginationResult => {
  const [searchParams, setSearchParams] = useState<SearchParameters>(() => {
    if (history.state && history.state.searchParams) {
      return history.state.searchParams;
    } else {
      const result: SearchParameters = {
        categories: [],
        memory: null,
        storage: null,
        cpu_brand: null,
        cpu: [],
        gpu_brand: null,
        gpu: [],
        price_range: [],
        skip_filter: null,
        page: 1,
        sort: "configured_price_pvp_with_vat asc",
        limit: PAGINATION_LIMIT,
      };
      const params = new URLSearchParams(window.location.search);
      params.forEach((value, key) => {
        if (key.startsWith("price_range.")) {
          const rangeIndex = parseInt(key.split(".")[2], 10);
          if (!result.price_range) {
            result.price_range = [];
          }
          if (!result.price_range[rangeIndex]) {
            result.price_range[rangeIndex] = {
              min_price: -1,
              max_price: -1,
            };
          }
          // @ts-ignore
          result.price_range[rangeIndex][key.split(".")[1]] = parseInt(
            value,
            10,
          );
        } else if (key === "sort") {
          result[key] = value;
        } else if (key === "skip_filter") {
          result[key] = value;
        } else if (key === "cpu_brand" || key === "gpu_brand") {
          result[key] = value.trim() !== "" ? value : null;
        } else if (key === "memory" || key === "storage") {
          result[key] = value.trim() !== "" ? value : null;
        } else if (key === "brand" || key === "stock_status") {
          result[key] = value.trim() !== "" ? value : null;
        } else if (key === "screen_size_min" || key === "screen_size_max") {
          const parsed = Number(value);
          result[key] = Number.isFinite(parsed) ? parsed : null;
        } else if (key === "categories") {
          result.categories = value
            .split(",")
            .map((v: string) => v.trim())
            .filter((v: string) => v !== "");
        } else if (key === "limit") {
          result[key] = parseInt(value, 10);
        } else if (key === "page") {
          result[key] = parseInt(value, 10);
        } else if (key === "cpu" || key === "gpu") {
          result[key] = value
            .split(",")
            .map((v: string) => v.trim())
            .filter((v: string) => v !== "");
        } else {
          // @ts-ignore
          result[key] = value;
        }
      });
      const categories = params.getAll("categories");
      if (categories.length > 0) {
        result.categories = categories
          .flatMap((value) => value.split(","))
          .map((value) => value.trim())
          .filter((value) => value !== "");
      }
      return result;
    }
  });

  const handleFilterChange = useCallback(
    (filter: string, value: string[] | string, brand: string | null) => {
      setSearchParams((prevParams) => {
        const updatedParams: SearchParameters = {
          ...prevParams,
          [filter]: value,
          page: 1,
        };
        if (brand?.trim()) {
          if (filter === "cpu") {
            updatedParams.cpu_brand = brand;
          } else if (filter === "gpu") {
            updatedParams.gpu_brand = brand;
          }
        } else if (filter === "cpu" && value == "" && updatedParams.cpu_brand) {
          updatedParams.cpu_brand = null;
        } else if (filter === "gpu" && value == "" && updatedParams.gpu_brand) {
          updatedParams.gpu_brand = null;
        }

        if (
          Object.keys(prevParams).some(
            (key) =>
              // @ts-ignore
              key !== "skip_filter" && prevParams[key] !== updatedParams[key],
          )
        ) {
          updatedParams.skip_filter = filter;
        }
        return updatedParams;
      });
    },
    [setSearchParams],
  );

  const resetFilter = useCallback(() => {
    setSearchParams((prevParams) => ({
      ...prevParams,
      cpu: [],
      gpu: [],
      gpu_brand: null,
      cpu_brand: null,
      memory: "",
      storage: "",
      price_range: [],
      search: "",
      page: 1,
    }));
  }, [setSearchParams]);

  const setPriceRange = useCallback(
    (priceRanges: PriceRange[]) => {
      setSearchParams((prevParams) => ({
        ...prevParams,
        price_range: priceRanges,
        page: 1,
      }));
    },
    [setSearchParams],
  );

  const handleSearch = useCallback(
    (searchTerm: string) => {
      setSearchParams((prevParams) => ({
        ...prevParams,
        search_text: searchTerm,
        page: 1,
      }));
    },
    [setSearchParams],
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      setSearchParams((prevParams) => ({
        ...prevParams,
        page: newPage,
      }));
    },
    [setSearchParams],
  );

  return {
    searchParams,
    resetFilter,
    setSearchParams,
    handleFilterChange,
    setPriceRange,
    handleSearch,
    handlePageChange,
  };
};

export default useSearchAndPagination;
